import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getRestaurants } from "@/lib/store";
import type { Restaurant } from "@/types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Calendar, Navigation, Edit3, ArrowLeft, Star, Utensils, Sparkles, HandCoins, ExternalLink } from "lucide-react";
import { whoWants } from "@/lib/utils";
import { buildAppleMapsLink } from "@/lib/maps";
import { Progress } from "@/components/ui/progress";
import { compositeScore20, scoreToneClass, scoreToneBadgeClasses, tasteAverageScore, tasteFineScore, tasteGrade } from "@/lib/rating";

const ViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [q] = useSearchParams();
  const fromTab = q.get('tab') === 'wishlist' ? 'wishlist' : 'visited';

  const [rev, setRev] = useState(0);

  useEffect(() => {
    const bump = () => setRev((x) => x + 1);
    window.addEventListener('storage', bump);
    window.addEventListener('ccq:restaurants-changed' as any, bump as any);
    return () => {
      window.removeEventListener('storage', bump);
      window.removeEventListener('ccq:restaurants-changed' as any, bump as any);
    };
  }, []);

  const current = useMemo<Restaurant | undefined>(() => {
    if (!id) return undefined;
    const list = getRestaurants();
    return list.find((r) => r.id === id) || list.find((r) => r.id?.startsWith?.(id));
  }, [id, rev]);

  const totalScore = useMemo(() => current ? compositeScore20(current) : 0, [current]);
  const tasteAverage = useMemo(() => current ? tasteAverageScore(current) : 0, [current]);
  const tasteFine = useMemo(() => current ? tasteFineScore(current) : 0, [current]);
  const tasteGradeValue = useMemo(() => current ? tasteGrade(current) : undefined, [current]);

  if (!id || !current) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="text-center space-y-4">
          <h1 className="text-2xl font-semibold">見つかりませんでした</h1>
          <Button onClick={() => navigate('/')}>一覧へ戻る</Button>
        </div>
      </div>
    );
  }

  const mapsUrl = buildAppleMapsLink(current.name, current.location, current.lat as any, current.lon as any, current.prefecture, current.municipality);

  return (
    <div className="min-h-screen bg-background p-3 sm:p-4">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-2">
          <Button variant="ghost" onClick={() => navigate(`/?${q.toString()}`)}>
            <ArrowLeft className="h-5 w-5 mr-1" /> 戻る
          </Button>
        </div>
        <div className="mb-3 flex flex-col gap-3 sm:mb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="break-words text-[22px] font-bold leading-snug tracking-tight sm:text-3xl">{current.name}</h1>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{current.cuisine}</Badge>
              {current.priceRange && <Badge variant="outline">{current.priceRange}</Badge>}
              {current.isWishlist && <Badge variant="outline">行きたい</Badge>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
            <Button variant="outline" onClick={() => navigate(`/edit/${id}`)}>
              <Edit3 className="h-4 w-4 mr-1" /> 編集する
            </Button>
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              <Button className="w-full bg-gradient-primary">
                <Navigation className="h-4 w-4 mr-1" /> Appleマップで開く
              </Button>
            </a>
            {current.sourceUrl && (
              <a href={current.sourceUrl} target="_blank" rel="noopener noreferrer" className="col-span-2 sm:col-span-1">
                <Button variant="secondary" className="w-full">
                  <ExternalLink className="h-4 w-4 mr-1" /> 参考URL
                </Button>
              </a>
            )}
            {current.tiktokUrl && (
              <a href={current.tiktokUrl} target="_blank" rel="noopener noreferrer" className="col-span-2 sm:col-span-1">
                <Button variant="outline" className="w-full">
                  <ExternalLink className="h-4 w-4 mr-1" /> TikTokを見る
                </Button>
              </a>
            )}
          </div>
        </div>

        <Card className="overflow-hidden border-0 shadow-card">
          {current.image && (
            <img src={current.image} alt={current.name} className="w-full h-56 sm:h-64 object-cover" />
          )}
          <div className="p-3 sm:p-4 space-y-3 sm:space-y-4">
            <div className="grid gap-2 sm:grid-cols-3">
              <div className={"rounded-md border p-3 shadow-sm " + scoreToneBadgeClasses(totalScore)}>
                <div className="text-xs text-muted-foreground/80">総合評価</div>
                <div className={"text-2xl font-bold " + scoreToneClass(totalScore)}>{totalScore}<span className="ml-1 text-sm font-normal">点</span></div>
              </div>
              <div className="rounded-md border bg-card p-3 shadow-sm">
                <div className="text-xs text-muted-foreground">味</div>
                <div className="text-2xl font-bold">{tasteAverage > 0 ? tasteAverage.toFixed(1) : '-'}</div>
              </div>
              <div className="rounded-md border bg-card p-3 shadow-sm">
                <div className="text-xs text-muted-foreground">場所</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {current.prefecture && <Badge variant="outline">{current.prefecture}</Badge>}
                  {current.municipality && <Badge variant="outline">{current.municipality}</Badge>}
                </div>
              </div>
            </div>

            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>{current.location}</span>
            </div>
            {current.visitDate && (
              <div className="text-sm text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>訪問 {current.visitDate}</span>
              </div>
            )}
            {Array.isArray(current.visitHistory) && current.visitHistory.length > 0 && (
              <div className="text-xs text-muted-foreground">訪問 {current.visitHistory.length}回</div>
            )}
            {current.isWishlist && Array.isArray(current.wishlistBy) && current.wishlistBy.length > 0 && (
              <div className="text-xs text-muted-foreground">食べたい: {whoWants(current.wishlistBy).join('・')}</div>
            )}

            {(current.ratingTaste || current.ratingAmbience || current.ratingService || current.ratingCost || current.ratingTasteBalance || current.ratingTasteIngredients || current.ratingTasteTechnique) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="flex items-center gap-1"><Utensils className="h-4 w-4"/>味</span>
                    <span>{tasteAverage > 0 ? `${tasteAverage.toFixed(1)} / 5` : '-'}</span>
                  </div>
                  <Progress value={Math.max(0, Math.min(100, tasteAverage * 20))} />
                  <div className="text-xs text-muted-foreground mt-1">
                    {tasteGradeValue ? `グレード ${tasteGradeValue}` : 'グレード 未評価'}
                    {tasteFine > 0 && ` ・9段階 ${tasteFine.toFixed(1)} / 9`}
                  </div>
                  {(Number(current.ratingTasteBalance) > 0 || Number(current.ratingTasteIngredients) > 0 || Number(current.ratingTasteTechnique) > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2 text-xs text-muted-foreground">
                      <div>バランス {current.ratingTasteBalance ?? '-'}</div>
                      <div>素材 {current.ratingTasteIngredients ?? '-'}</div>
                      <div>丁寧さ {current.ratingTasteTechnique ?? '-'}</div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-1"><span className="flex items-center gap-1"><Sparkles className="h-4 w-4"/>雰囲気</span><span>{current.ratingAmbience ?? '-'} / 5</span></div>
                  <Progress value={(Number(current.ratingAmbience)||0) * 20} />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-1"><span className="flex items-center gap-1"><Star className="h-4 w-4"/>サービス</span><span>{current.ratingService ?? '-'} / 5</span></div>
                  <Progress value={(Number(current.ratingService)||0) * 20} />
                </div>
                <div>
                  <div className="flex items-center justify-between text-sm mb-1"><span className="flex items-center gap-1"><HandCoins className="h-4 w-4"/>コスパ</span><span>{current.ratingCost ?? '-'} / 5</span></div>
                  <Progress value={(Number(current.ratingCost)||0) * 20} />
                </div>
              </div>
            )}

            {current.notes && (
              <p className="text-card-foreground leading-relaxed whitespace-pre-wrap">
                {current.notes}
              </p>
            )}

            {(current.images && current.images.length > 0) && (
              <div className="space-y-2">
                <div className="text-sm text-muted-foreground">写真</div>
                <div className="grid grid-cols-3 gap-2">
                  {current.images.map((u, idx) => (
                    <img key={u+idx} src={u} alt={`photo-${idx+1}`} className="w-full h-24 object-cover rounded" />
                  ))}
                </div>
              </div>
            )}

            {(current.ratingTaste || current.ratingAmbience || current.ratingService || current.ratingCost || tasteFine > 0) && (
              <div className="text-sm text-muted-foreground flex flex-wrap gap-2">
                <span>味 {tasteAverage > 0 ? `${tasteAverage.toFixed(1)} / 5` : '-'}</span>
                {tasteGradeValue && <span>グレード {tasteGradeValue}</span>}
                {tasteFine > 0 && <span>9段階 {tasteFine.toFixed(1)}</span>}
                <span>雰 {current.ratingAmbience ?? '-'}</span>
                <span>接 {current.ratingService ?? '-'}</span>
                <span>価 {current.ratingCost ?? '-'}</span>
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ViewPage;
